// The "there is more" affordance under an Explorer table.
//
// It answers TWO different questions with one control, deliberately, because a
// reader scrolling a list does not care which one they hit:
//
//   * rows already fetched but not yet painted (`use-paged-rows` windows them,
//     because painting thousands of <tr>s at once makes the tab crawl); and
//   * rows still on the server, behind a keyset cursor.
//
// The local window is always drained first — it is free — and only then is the
// next page fetched. Two separate buttons would make the operator learn a
// distinction that is ours, not theirs.
//
// It also auto-advances: a sentinel asks for the next chunk when it scrolls into
// view, so the list reads as infinite scroll while staying strictly paged
// underneath (one bounded request per page, never a stream). The button stays for
// keyboard users and for the case where the observer never fires — a sentinel
// inside a container that does not scroll is simply never intersected, and the
// reader must still be able to ask.
//
// The sentinel carries a CLASS as HARDENING, not as a proven fix. This wrap is a
// flex row, so an unstyled empty div in it collapses to ZERO WIDTH, and a
// zero-area target is at best unreliable as an observer target: whether an empty
// intersection rect counts as intersecting is exactly the corner engines disagree
// on. `.ex-load-more__sentinel` gives it real area (it spans the wrap, one pixel
// tall, absolutely positioned so the row's layout is unchanged) and removes the
// question. Whether the old zero-width sentinel actually failed in a real browser
// is UNVERIFIED — the only environment available here reports `document.hidden`,
// and Chrome suspends observer delivery for a hidden page, so no callback arrives
// for any element, a full-size `document.body` included. Do not read this comment
// as evidence the auto-advance was broken; read it as the reason not to leave a
// zero-area target in place while we cannot tell.
//
// `rootMargin` then asks one screen EARLY, which is what makes it read as
// infinite scroll rather than as a stall at the bottom of every page.
import { Button } from "@recruiter/shared";
import { useEffect, useRef } from "react";

export function ShowMoreRow({
  remaining,
  onClick,
  hasMore = false,
  onLoadMore,
  loading = false,
  label = "rows",
}: {
  /** Rows fetched but not painted — the local window's backlog. */
  remaining: number;
  onClick: () => void;
  /** Whether the SERVER has another page after everything loaded. */
  hasMore?: boolean;
  onLoadMore?: () => void;
  loading?: boolean;
  /** What the rows are, for the fetch button ("projects", "people"). */
  label?: string;
}) {
  const sentinel = useRef<HTMLDivElement>(null);
  // The local window first, the server only once it is drained.
  const advance = remaining > 0 ? onClick : hasMore && !loading ? onLoadMore : undefined;
  const advanceRef = useRef(advance);
  advanceRef.current = advance;

  useEffect(() => {
    const node = sentinel.current;
    // `IntersectionObserver` is absent in jsdom, so the effect no-ops under test
    // and the button — which is what the tests drive — is unaffected.
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Read through the ref: the callback outlives the render that made it, and
        // firing a STALE `advance` would refetch the page just fetched.
        if (entries.some((e) => e.isIntersecting)) advanceRef.current?.();
      },
      { rootMargin: "400px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  if (remaining <= 0 && !hasMore) return null;
  return (
    <div className="ex-load-more">
      <div ref={sentinel} className="ex-load-more__sentinel" aria-hidden="true" />
      {remaining > 0 ? (
        <Button type="button" variant="outline" tone="neutral" size="sm" onClick={onClick}>
          Show more ({remaining.toLocaleString("en-US")} left)
        </Button>
      ) : (
        <Button
          type="button"
          variant="outline"
          tone="neutral"
          size="sm"
          disabled={loading}
          onClick={() => onLoadMore?.()}
        >
          {loading ? "Loading…" : `Load more ${label}`}
        </Button>
      )}
    </div>
  );
}
